import { useState, useCallback } from "react";
import { getForm, postForm, getMerchant, uploadFiles, getSurveyForm } from "../apis/formApi"; // Added uploadFiles
import { DOMAIN, getSurveyEmail, getSurveyFormName } from "../utils/constants";
import { compareDate } from "../utils/functions/compareDate";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const useSurveyForm = () => {
  const [fetching, setFetching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDraftLoading, setIsDraftLoading] = useState(false);
  
  const [data, setData] = useState(null);
  const [merchant, setMerchant] = useState(null);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitData, setSubmitData] = useState(null);
  const [formExpired, setFormExpired] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const { executeRecaptcha } = useGoogleReCaptcha();

  // 1. Fetch Form Structure and Merchant Data
  const fetch = useCallback(async (email='') => {
    setFetching(true);
    setError(null);
    try {
      const [merchantResult, result] = await Promise.all([
        getMerchant(),
        getSurveyForm({ name: getSurveyFormName(), email: email?.replace(/ /g, "_").toLowerCase()}),
      ]);

      if (!result.ok || !result.data.success) {
        throw new Error("Unable to load survey questions.");
      }

      const formData = result?.data?.data?.[0];
      if (formData && formData?.end_date) {
        setFormExpired(compareDate(formData.end_date, "<"));
      }
      if (formData && formData?.is_submitted) {
        setFormSubmitted(true);
      }

      setMerchant(merchantResult?.data?.merchant || null);
      setData(result.data);
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setFetching(false);
    }
  }, []);

  // 2. Draft Loading Logic (Placeholder connected to backend)
  const loadDraft = useCallback(async (token) => {
    setIsDraftLoading(true);
    try {
      // const result = await getSurveyDraft({ formName: getSurveyFormName(), token });
      // return (result.ok && result.data.success) ? result.data.data.values : null;
      return null; 
    } catch (err) {
      console.error("Failed to load draft:", err);
      return null;
    } finally {
      setIsDraftLoading(false);
    }
  }, []);
        

  // 3. File Data Helper
  const makeFormData = (ticket_id, files) => {
    const formData = new FormData();
    formData.append("ticket_id", ticket_id);
    formData.append("source", 'webform-survey');
    files.forEach((file) => {
      // Assuming file object structure { value: File }
      formData.append("file[]", file.value, file.value.name);
    });
    return formData;
  };

  // 4. Main Submit/Save Function
  const submitSurvey = useCallback(async ({ 
    accessToken,
    data, 
    is_submitted = false,
    files = [], 
    token, 
    formikOptions, 
    merchantData 
  }) => {
    setSubmitError(null);
    
    // Determine which loading state to trigger
    const setLoading = is_submitted ? setIsSubmitting : setIsDraftLoading;
    if (formikOptions?.setSubmitting) formikOptions.setSubmitting(true);
    setLoading(true);

    try {
      const email = getSurveyEmail().replace(/ /g, "_").toLowerCase();
      console.log("Submitting survey with values:", data, "and files:", files);
      
      // Format fields for the API
      const fields = Object.entries(data.fields).map(([name, value]) => ({
        name,
        value: value ?? "" 
      }));

      const submissionPayload = {
        ...data,
        fields,
        is_submitted,
        tenantId: merchantData?.tenantId,
        email,
        type: 'capture',
      };

      const result = await postForm({ 
        data: submissionPayload, 
        token, 
        accessToken,
        type: 'capture',
        tenantId: merchantData?.tenantId
      });

      if (!result.ok || (result.data && !result.data.success)) {
        throw new Error(result.data?.data?.message || "Submission failed");
      }

      // Handle File Uploads if a ticket was created
      if (files.length > 0 && result.data.data.ticket_id) {
        const formData = makeFormData(result.data.data.ticket_id, files);
        const uploadResult = await uploadFiles({ token, data: formData });
        
        if (!uploadResult.ok) {
          throw new Error("Form submitted, but file upload failed.");
        }
      }

      setSubmitData(result.data);
      return result.data;

    } catch (err) {
      setSubmitError(err.message);
      return null;
    } finally {
      setLoading(false);
      if (formikOptions?.setSubmitting) formikOptions.setSubmitting(false);
    }
  }, []);

  const saveDraft = useCallback((args) => submitSurvey({ ...args, is_submitted: false }), [submitSurvey]);

  return {
    fetching,
    isSubmitting,
    isDraftLoading,
    data,
    merchant,
    error,
    submitData,
    submitError,
    fetch,
    saveDraft,
    loadDraft,
    submitSurvey,
    formExpired,
    formSubmitted,
  };
};

export default useSurveyForm;